import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ClipboardCheck, Calendar, Users, CheckCircle, XCircle, 
  Clock, AlertCircle, Download, Eye, RefreshCw, FileText,
  UserCheck, UserX, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

const AttendanceManagementPage = ({ onMenuClick }) => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetDetail, setSheetDetail] = useState(null);
  
  // Generation modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');

  const fetchSheets = useCallback(async () => {
    try {
      let url = '/attendance/sheets?';
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      if (selectedGroup) url += `group_id=${selectedGroup}&`;
      if (selectedStatus) url += `status=${selectedStatus}&`;
      
      const response = await api.get(url);
      setSheets(response.data);
    } catch (error) {
      console.error('Erreur chargement émargements:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedGroup, selectedStatus]);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Erreur chargement groupes:', error);
    }
  };

  useEffect(() => {
    fetchSheets();
    fetchGroups();
  }, [fetchSheets]);

  const openDetailModal = async (sheet) => {
    setSelectedSheet(sheet);
    setShowDetailModal(true);
    try {
      const response = await api.get(`/attendance/sheets/${sheet.id}`);
      setSheetDetail(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const updateStudentStatus = async (studentId, status) => {
    try {
      await api.put(`/attendance/sheets/${selectedSheet.id}/student/${studentId}/status`, { status });
      // Refresh detail
      const response = await api.get(`/attendance/sheets/${selectedSheet.id}`);
      setSheetDetail(response.data);
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSheets = async () => {
    if (!generateStartDate || !generateEndDate) {
      toast.error('Veuillez sélectionner les dates');
      return;
    }
    try {
      const response = await api.post(`/attendance/generate-all?start_date=${generateStartDate}&end_date=${generateEndDate}`);
      toast.success(response.data.message);
      setShowGenerateModal(false);
      fetchSheets();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  const exportPdf = async (sheetId) => {
    try {
      const response = await api.get(`/attendance/sheets/${sheetId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emargement_${sheetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-amber-100 text-amber-700',
      validated: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminé',
      validated: 'Validé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStudentStatusBadge = (status) => {
    const config = {
      present: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Présent', icon: CheckCircle },
      absent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Absent', icon: XCircle },
      excused: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Excusé', icon: AlertCircle },
      late: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Retard', icon: Clock }
    };
    const c = config[status] || config.absent;
    const Icon = c.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  // Stats
  const totalSheets = sheets.length;
  const validatedSheets = sheets.filter(s => s.status === 'validated').length;
  const totalPresent = sheets.reduce((sum, s) => sum + (s.present_count || 0), 0);
  const totalStudents = sheets.reduce((sum, s) => sum + (s.total_students || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8 text-violet-600" />
              Émargement
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Gestion des feuilles de présence
            </p>
          </div>
          
          <Button 
            onClick={() => setShowGenerateModal(true)}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Générer les feuilles
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-violet-50 dark:bg-violet-900/20 border-violet-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-700">{totalSheets}</p>
                  <p className="text-xs text-violet-600">Feuilles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{validatedSheets}</p>
                  <p className="text-xs text-emerald-600">Validées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <UserCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{totalPresent}</p>
                  <p className="text-xs text-blue-600">Présences</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">
                    {totalStudents > 0 ? Math.round((totalPresent / totalStudents) * 100) : 0}%
                  </p>
                  <p className="text-xs text-amber-600">Taux présence</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Filter className="w-5 h-5 text-slate-500" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                placeholder="Date début"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                placeholder="Date fin"
              />
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">Tous les groupes</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="validated">Validé</option>
              </select>
              <Button variant="outline" onClick={fetchSheets}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sheets List */}
        <Card>
          <CardHeader>
            <CardTitle>Feuilles d'émargement</CardTitle>
          </CardHeader>
          <CardContent>
            {sheets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune feuille d'émargement</p>
                <p className="text-sm mt-2">Cliquez sur "Générer les feuilles" pour créer les feuilles automatiquement</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Cours</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Groupe</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Formateur</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Présents</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheets.map(sheet => (
                      <tr key={sheet.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{new Date(sheet.event_date).toLocaleDateString('fr-FR')}</span>
                            <span className="text-slate-500 text-sm">{sheet.start_time}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-slate-900 dark:text-white">{sheet.event_title}</span>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{sheet.group_name || '-'}</td>
                        <td className="py-3 px-4 text-slate-600">{sheet.trainer_name || '-'}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-medium text-emerald-600">{sheet.present_count}</span>
                          <span className="text-slate-400">/{sheet.total_students}</span>
                        </td>
                        <td className="py-3 px-4 text-center">{getStatusBadge(sheet.status)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(sheet)}
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => exportPdf(sheet.id)}
                              title="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-violet-600" />
              Feuille d'émargement
            </DialogTitle>
            <DialogDescription>
              {sheetDetail?.event_title} - {sheetDetail?.event_date && new Date(sheetDetail.event_date).toLocaleDateString('fr-FR')}
            </DialogDescription>
          </DialogHeader>
          
          {sheetDetail && (
            <div className="space-y-6 py-4">
              {/* Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Groupe</p>
                  <p className="font-medium">{sheetDetail.group_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Formateur</p>
                  <p className="font-medium">{sheetDetail.trainer_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Horaire</p>
                  <p className="font-medium">{sheetDetail.start_time} - {sheetDetail.end_time}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Statut</p>
                  {getStatusBadge(sheetDetail.status)}
                </div>
              </div>
              
              {/* Summary */}
              <div className="flex items-center gap-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-600">{sheetDetail.present_count} présents</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserX className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-600">{sheetDetail.absent_count} absents</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-600">{sheetDetail.excused_count} excusés</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span className="font-medium text-orange-600">{sheetDetail.late_count} retards</span>
                </div>
              </div>
              
              {/* Signatures */}
              <div>
                <h4 className="font-medium mb-3">Liste des étudiants</h4>
                <div className="space-y-2">
                  {sheetDetail.signatures?.map(sig => (
                    <div 
                      key={sig.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900 rounded-full flex items-center justify-center">
                          <span className="text-violet-600 font-medium">
                            {sig.student_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{sig.student_name || `Étudiant #${sig.student_id}`}</p>
                          {sig.signed_at && (
                            <p className="text-xs text-slate-500">
                              Signé à {new Date(sig.signed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {getStudentStatusBadge(sig.status)}
                        
                        {/* Status change buttons */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => updateStudentStatus(sig.student_id, 'present')}
                            className={`p-1.5 rounded ${sig.status === 'present' ? 'bg-emerald-100' : 'hover:bg-emerald-50'}`}
                            title="Marquer présent"
                          >
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          </button>
                          <button
                            onClick={() => updateStudentStatus(sig.student_id, 'absent')}
                            className={`p-1.5 rounded ${sig.status === 'absent' ? 'bg-red-100' : 'hover:bg-red-50'}`}
                            title="Marquer absent"
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                          </button>
                          <button
                            onClick={() => updateStudentStatus(sig.student_id, 'excused')}
                            className={`p-1.5 rounded ${sig.status === 'excused' ? 'bg-amber-100' : 'hover:bg-amber-50'}`}
                            title="Marquer excusé"
                          >
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                          </button>
                          <button
                            onClick={() => updateStudentStatus(sig.student_id, 'late')}
                            className={`p-1.5 rounded ${sig.status === 'late' ? 'bg-orange-100' : 'hover:bg-orange-50'}`}
                            title="Marquer en retard"
                          >
                            <Clock className="w-4 h-4 text-orange-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Trainer validation */}
              {sheetDetail.trainer_signed && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Validé par le formateur</span>
                  </div>
                  {sheetDetail.trainer_signed_at && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Le {new Date(sheetDetail.trainer_signed_at).toLocaleString('fr-FR')}
                    </p>
                  )}
                  {sheetDetail.trainer_comment && (
                    <p className="text-sm mt-2">{sheetDetail.trainer_comment}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => exportPdf(selectedSheet?.id)}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </Button>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Générer les feuilles d'émargement</DialogTitle>
            <DialogDescription>
              Les feuilles seront créées pour tous les cours dans la période sélectionnée
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Date de début</label>
              <Input
                type="date"
                value={generateStartDate}
                onChange={(e) => setGenerateStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Date de fin</label>
              <Input
                type="date"
                value={generateEndDate}
                onChange={(e) => setGenerateEndDate(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>
              Annuler
            </Button>
            <Button onClick={generateSheets} className="bg-violet-600 hover:bg-violet-700">
              Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceManagementPage;
